// ─────────────────────────────────────────────
// src/pages/AboutPage.jsx
// ─────────────────────────────────────────────
import { useEffect } from "react";
import { StarField, GlassCard, Btn, Badge, SectionLabel, SectionTitle } from "../components/UI";
import Footer from "../components/Footer";
import { LEARNING_AREAS, TEAM } from "../data/constants";

export default function AboutPage({ setPage }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = '__about-schema';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Mars Academy",
      "url": "https://www.marsacademy.ai/",
      "description": "Mars Academy is an IDCA-certified STEM enrichment provider serving elementary and middle school students in Fairfax County, Virginia and Northern Virginia with hands-on coding, AI, internet safety, and digital infrastructure programs.",
      "about": {
        "@type": "EducationalOrganization",
        "@id": "https://www.marsacademy.ai/#organization"
      }
    });
    document.head.appendChild(script);
    return () => document.getElementById('__about-schema')?.remove();
  }, []);

  return (
    <div>
      <section style={{ position:"relative", padding:"8rem 2rem 5rem", overflow:"hidden" }}>
        <StarField count={50} />
        <div style={{ maxWidth:860, margin:"0 auto", textAlign:"center", position:"relative", zIndex:2 }}>
          <SectionLabel color="#9B5CF6">Our Mission</SectionLabel>
          <h1 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"clamp(2rem,6vw,3.8rem)", lineHeight:1.2, marginBottom:"1.4rem" }}>
            We Want Kids to Be <span style={{ color:"#FFD93D" }}>Ahead of Their Time</span> 🌍
          </h1>
          <p style={{ color:"rgba(255,255,255,0.65)", fontSize:"1.08rem", lineHeight:1.85, fontWeight:600, maxWidth:680, margin:"0 auto 0.8rem" }}>
            Our mission is to teach the young generation the technology that will define their future — before that future arrives.
          </p>
          <p style={{ color:"rgba(255,255,255,0.52)", fontSize:"1rem", lineHeight:1.85, fontWeight:600, maxWidth:640, margin:"0 auto 2rem" }}>
            From coding and AI to digital infrastructure and cybersecurity, Mars Academy ensures every student graduates with real skills and a real head start on tomorrow. We want them to be ahead of their time.
          </p>
          <Btn onClick={() => setPage("login")} variant="primary">🚀 Sign Up Now</Btn>
        </div>
      </section>

      {/* Story + approach */}
      <section style={{ padding:"4rem 2rem", maxWidth:1100, margin:"0 auto" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:"3rem" }}>
          <div>
            <SectionLabel color="#FF6B6B">Our Story</SectionLabel>
            <SectionTitle>Why We Started</SectionTitle>
            <p style={{ color:"rgba(255,255,255,0.55)", lineHeight:1.8, fontWeight:600, marginBottom:"1.1rem" }}>
              Mars Academy was founded by educators and engineers who noticed a gap: kids were using technology every day but had no idea how it worked or how to stay safe online.
            </p>
            <p style={{ color:"rgba(255,255,255,0.55)", lineHeight:1.8, fontWeight:600 }}>
              Our space theme is a metaphor — we're launching young minds into uncharted territory, equipping them with tools to explore and thrive in a digital universe.
            </p>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:"0.9rem" }}>
            {[
              { icon:"🎯", title:"Hands-On Learning",       desc:"Every concept is immediately applied. Kids code, build, and create — no passive watching." },
              { icon:"🔬", title:"Inquiry-Based Approach",   desc:"We ask 'why' before 'how'. Curiosity drives every lesson." },
              { icon:"🤝", title:"Collaborative Projects",   desc:"Kids work in teams, building communication alongside tech skills." },
              { icon:"🏆", title:"Badge-Based Progress",     desc:"Visual achievements motivate kids and give parents clear insight." },
            ].map((v, i) => (
              <GlassCard key={i} style={{ padding:"1.15rem" }}>
                <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                  <div style={{ fontSize:"1.5rem", flexShrink:0 }}>{v.icon}</div>
                  <div>
                    <div style={{ fontWeight:800, fontSize:"0.92rem", marginBottom:3 }}>{v.title}</div>
                    <div style={{ color:"rgba(255,255,255,0.48)", fontSize:"0.82rem", fontWeight:600, lineHeight:1.6 }}>{v.desc}</div>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="founder-section" style={{ padding:"4rem 2rem", maxWidth:1100, margin:"0 auto" }}>
        <div className="founder-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:"3rem", alignItems:"center" }}>
          <div className="founder-photo-wrap" style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"1.3rem" }}>
            <img
              src="/ali-paryavi-founder.jpg"
              alt="Ali Paryavi, Founder of Mars Academy"
              width={420}
              height={420}
              loading="lazy"
              className="founder-photo"
              style={{ width:"100%", maxWidth:380, aspectRatio:"1/1", objectFit:"cover", objectPosition:"50% 20%", borderRadius:24, border:"1.5px solid rgba(255,217,61,0.25)", boxShadow:"0 16px 44px rgba(0,0,0,0.4)" }}
            />
            <GlassCard style={{ width:"100%", maxWidth:380, padding:"1.4rem 1.6rem", textAlign:"center", boxSizing:"border-box" }}>
              <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.15rem", marginBottom:"0.15rem" }}>Ali Paryavi</div>
              <div style={{ color:"#FFD93D", fontWeight:700, fontSize:"0.82rem", marginBottom:"1.1rem" }}>Founder, Mars Academy</div>
              <div style={{ borderTop:"1px solid rgba(255,255,255,0.1)", paddingTop:"1rem" }}>
                <div style={{ color:"rgba(255,255,255,0.38)", fontSize:"0.68rem", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"0.75rem" }}>
                  Education &amp; Professional Certifications
                </div>
                <div style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.83rem", fontWeight:600, marginBottom:"0.85rem" }}>
                  University of Maryland Global Campus
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"center", gap:"0.4rem", marginBottom:"0.6rem" }}>
                  <Badge color="#9B5CF6" small>CSPO®</Badge>
                  <Badge color="#10B981" small>DCIE</Badge>
                  <Badge color="#06B6D4" small>DCIS</Badge>
                </div>
                <div style={{ color:"rgba(255,255,255,0.35)", fontSize:"0.68rem", fontWeight:600, lineHeight:1.55 }}>
                  Certified Scrum Product Owner® · Certified Data Center Infrastructure Expert · Certified Data Center Infrastructure Specialist
                </div>
              </div>
            </GlassCard>
          </div>
          <div>
            <SectionLabel color="#FFD93D">Meet the Founder</SectionLabel>
            <SectionTitle>Ali Paryavi, Founder of Mars Academy</SectionTitle>
            <p style={{ color:"rgba(255,255,255,0.55)", lineHeight:1.8, fontWeight:600, marginBottom:"1.1rem" }}>
              Ali Paryavi founded Mars Academy to close the gap between how much time kids spend on technology and how little most of them understand about it. He holds a bachelor's degree in cybersecurity from the University of Maryland Global Campus, and the team he's built brings over a decade of combined experience in the technology and cybersecurity industry to every program Mars Academy runs.
            </p>
            <GlassCard className="founder-quote" style={{ padding:"1.4rem 1.6rem", borderLeft:"3px solid #FFD93D" }}>
              <p style={{ color:"rgba(255,255,255,0.68)", fontStyle:"italic", lineHeight:1.8, fontWeight:600, marginBottom:"0.7rem" }}>
                "Mars Academy gives students more than just screen time. We teach them how technology works and how to create with it. Students build games, learn coding, explore AI, and develop a foundation in computer technology, cybersecurity, cloud computing, and digital infrastructure."
              </p>
              <div style={{ color:"#FFD93D", fontWeight:800, fontSize:"0.85rem" }}>— Ali Paryavi, Founder</div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section style={{ padding:"4rem 2rem", background:"rgba(255,255,255,0.025)", borderTop:"1px solid rgba(255,255,255,0.07)", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ maxWidth:1000, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:"2.8rem" }}>
            <SectionLabel color="#06B6D4">Curriculum</SectionLabel>
            <SectionTitle center>What Kids Learn in One Program 🧠</SectionTitle>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:"1rem" }}>
            {LEARNING_AREAS.map((a, i) => (
              <div key={i} style={{ background:a.color+"12", border:`1.5px solid ${a.color}28`, borderRadius:18, padding:"1.4rem 1.2rem", textAlign:"center" }}>
                <div style={{ fontSize:"1.9rem", marginBottom:"0.55rem" }}>{a.icon}</div>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"0.97rem", color:a.color, marginBottom:"0.45rem" }}>{a.title}</div>
                <div style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.78rem", fontWeight:600, lineHeight:1.5 }}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IDCA Certification */}
      <section style={{ padding:"4rem 1rem" }}>
        <div style={{ maxWidth:1000, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:"2.8rem" }}>
            <SectionLabel color="#10B981">Certified Curriculum</SectionLabel>
            <SectionTitle center>Certified by the International Data Center Authority</SectionTitle>

            {/* IDCA Logo */}
            <div style={{ display:"flex", justifyContent:"center", margin:"2rem auto 2.2rem", padding:"0 1rem" }}>
              <div style={{
                background:"linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(6,182,212,0.08) 100%)",
                border:"1.5px solid rgba(16,185,129,0.35)",
                borderRadius:24,
                padding:"1.2rem clamp(1rem, 5vw, 2.8rem)",
                display:"flex",
                alignItems:"center",
                justifyContent:"center",
                boxShadow:"0 0 40px rgba(16,185,129,0.18), 0 0 80px rgba(6,182,212,0.08)",
                backdropFilter:"blur(12px)",
                maxWidth:"100%",
                boxSizing:"border-box",
              }}>
                <img
                  src="/idca-mars-logo.png"
                  alt="International Data Center Authority (IDCA) Logo"
                  style={{ height:"clamp(40px, 10vw, 70px)", width:"auto", maxWidth:"100%", objectFit:"contain", filter:"brightness(4) contrast(1.2)" }}
                />
              </div>
            </div>

            <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"clamp(0.88rem, 2.5vw, 1rem)", lineHeight:1.85, fontWeight:600, maxWidth:680, margin:"0 auto 1.8rem", padding:"0 0.5rem" }}>
              Mars Academy's curriculum has been reviewed and certified by the{" "}
              <span style={{ color:"#10B981", fontWeight:800 }}>International Data Center Authority (IDCA)</span> — the world's leading global digital economy think tank and the gold standard for data center and digital infrastructure education.
            </p>

            <a href="https://www.idc-a.org" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"flex", justifyContent:"center" }}>
              <div style={{
                display:"inline-flex",
                alignItems:"center",
                gap:"0.6rem",
                padding:"0.85rem clamp(1.2rem, 4vw, 2rem)",
                borderRadius:50,
                background:"linear-gradient(135deg, #10B981 0%, #06B6D4 100%)",
                boxShadow:"0 4px 24px rgba(16,185,129,0.35), 0 2px 8px rgba(6,182,212,0.2)",
                fontWeight:800,
                fontSize:"clamp(0.85rem, 2.5vw, 0.95rem)",
                color:"#fff",
                letterSpacing:"0.02em",
                transition:"transform 0.15s, box-shadow 0.15s",
                cursor:"pointer",
                whiteSpace:"nowrap",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 8px 32px rgba(16,185,129,0.45), 0 4px 12px rgba(6,182,212,0.3)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 4px 24px rgba(16,185,129,0.35), 0 2px 8px rgba(6,182,212,0.2)"; }}
              >
                <span>Check Them Out</span>
                <span style={{ fontSize:"1.1rem" }}>↗</span>
              </div>
            </a>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:"1.4rem", marginBottom:"2.5rem" }}>
            {[
              {
                icon:"🏛️",
                title:"Who Is IDCA?",
                desc:"The International Data Center Authority is the foremost global authority on data center design, operations, AI infrastructure, and digital economy policy. IDCA sets the standards that industry professionals and governments worldwide follow.",
                color:"#10B981",
              },
              {
                icon:"📋",
                title:"What Certification Means",
                desc:"IDCA-certified curriculum has been independently reviewed against rigorous industry standards. This means the knowledge we teach your child reflects the same frameworks used by real-world data centers, cloud providers, and technology enterprises.",
                color:"#06B6D4",
              },
              {
                icon:"🚀",
                title:"Why It Matters for Your Child",
                desc:"Most kids learn tech skills with no recognized framework behind them. Our IDCA certification ensures your child is building knowledge that is structured, credible, and directly aligned with where the tech industry is heading — giving them a genuine head start.",
                color:"#9B5CF6",
              },
            ].map((v, i) => (
              <GlassCard key={i} style={{ padding:"1.4rem", borderTop:`3px solid ${v.color}` }}>
                <div style={{ fontSize:"2rem", marginBottom:"0.7rem" }}>{v.icon}</div>
                <div style={{ fontWeight:800, fontSize:"1rem", marginBottom:"0.6rem", color:v.color }}>{v.title}</div>
                <div style={{ color:"rgba(255,255,255,0.52)", fontSize:"0.85rem", fontWeight:600, lineHeight:1.7 }}>{v.desc}</div>
              </GlassCard>
            ))}
          </div>

          <GlassCard style={{ padding:"1.4rem clamp(1rem, 4vw, 2.4rem)", display:"flex", alignItems:"flex-start", gap:"1.2rem", flexWrap:"wrap" }}>
            <div style={{ fontSize:"2.2rem", flexShrink:0 }}>🔒</div>
            <div style={{ flex:1, minWidth:200 }}>
              <div style={{ fontWeight:800, fontSize:"1.05rem", marginBottom:"0.4rem" }}>
                A Standard Parents Can Trust
              </div>
              <div style={{ color:"rgba(255,255,255,0.52)", fontSize:"0.88rem", fontWeight:600, lineHeight:1.75 }}>
                IDCA's certification process evaluates curriculum for technical accuracy, industry relevance, and educational integrity. When you enroll your child at Mars Academy, you are choosing a program built on internationally recognized standards — not just someone's idea of what kids should learn. Our certification covers the full scope of our digital infrastructure, AI, and cybersecurity content, so every lesson your child completes is part of a verified, world-class learning path.
              </div>
            </div>
          </GlassCard>
        </div>
      </section>

      <Footer setPage={setPage} />
    </div>
  );
}
